/**
 * Projectile shape, flight and colour. Pure, Babylon-free, tested.
 *
 * A cast resolves **instantly** on the server: by the time `EVENT_SKILL_CAST`
 * arrives the damage is already applied. So a projectile is decoration over a
 * settled outcome, which is what makes it safe to do entirely on the client — and
 * what decides its one hard rule: it must *arrive*, because a bolt that is still
 * in the air when the damage number pops reads as a miss.
 *
 * Every number here follows from that. Flight is a fixed duration rather than a
 * fixed speed, so the bolt lands on time regardless of distance, and it is short
 * enough that the arrival is close to the damage. **That duration is one constant
 * for every shape**: a shape may change what a spell looks like, never when it
 * lands.
 *
 * The shape itself comes from the catalogue — `visual_assets.projectile_shape` in
 * `skills.yaml`, carried on `SkillInfo`. This file turns that string into geometry,
 * a trail and an impact; `bolts.ts` builds the meshes from it.
 */

import type { WeaponClass } from '../net/protocol';

/** How long a bolt takes to cross, at any distance. */
export const BOLT_SECONDS = 0.18;

/** How long the impact flash lingers after arrival. */
export const IMPACT_SECONDS = 0.22;

/**
 * How long a blast ring takes to open and fade, in seconds.
 *
 * Longer than an impact flash (0.22) because it is answering a different question. A
 * flash says *this one died*; a ring says *everything inside here was hit*, and the
 * player has to be able to read its extent before it goes — which means it must still be
 * on screen after the damage numbers have appeared over four separate heads.
 */
export const BLAST_SECONDS = 0.42;

/** Height above the ground a bolt flies at — roughly chest height. */
export const BOLT_HEIGHT = 1.1;

/** How high the arc rises at its midpoint, in world units. */
export const BOLT_ARC = 0.55;

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/**
 * Where a bolt is at `t` in 0..1, on a shallow arc between two ground points.
 *
 * An arc rather than a straight line because the camera looks down: a bolt
 * travelling flat across the floor plane is a dot that barely moves in screen
 * space, and the rise is what makes the motion legible from above.
 *
 * `out` exists so the render loop allocates nothing. Twenty-four bolts, each with a
 * head and up to eight trail motes, is a couple of hundred short-lived objects per
 * frame if this returns a fresh one — the same reason the Babylon side reuses
 * `Vector3`s through `LerpToRef`. It is optional only so a test can read a position
 * without keeping a scratch.
 */
export function boltAt(
  from: { x: number; z: number },
  to: { x: number; z: number },
  t: number,
  out?: Vec3,
): Vec3 {
  const k = clamp01(t);
  // A parabola peaking at the midpoint: 0 at both ends, 1 halfway.
  const lift = 4 * k * (1 - k);
  const target = out ?? { x: 0, y: 0, z: 0 };
  target.x = from.x + (to.x - from.x) * k;
  target.y = BOLT_HEIGHT + BOLT_ARC * lift;
  target.z = from.z + (to.z - from.z) * k;
  return target;
}

/**
 * The yaw that points a mesh's local +Z along the flight, in radians.
 *
 * Every elongated shape needs it — a lance drawn across its own path is a wall, not a
 * beam — and so does the impact for any shape whose flash is not round.
 */
export function boltHeading(from: { x: number; z: number }, to: { x: number; z: number }): number {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  // A cast with no direction has no heading; zero is as good as any and keeps the
  // caller free of a special case.
  if (dx === 0 && dz === 0) return 0;
  return Math.atan2(dx, dz);
}

/**
 * How large the impact flash is at `t` in 0..1 of its life, in world units.
 *
 * Expands and fades rather than simply fading: a sphere that only dims looks like
 * a light being switched off, and one that only grows looks like an explosion that
 * never ends.
 */
export function impactScale(t: number): number {
  const k = clamp01(t);
  return 0.35 + 1.15 * easeOut(k);
}

/** Impact opacity at `t`. Reaches zero exactly at the end, never before. */
export function impactAlpha(t: number): number {
  return 1 - clamp01(t);
}

/**
 * Parses `#rrggbb` into a 0..1 triple, or returns the fallback.
 *
 * Forgiving on purpose: a colour is content, a typo in it is silent, and a skill
 * with a malformed palette should be the wrong colour rather than invisible or a
 * crash. Accepts `#rgb` too, since that is what a hand-edited YAML tends to grow.
 */
export function parseHex(
  hex: string | undefined,
  fallback: [number, number, number],
): [number, number, number] {
  if (!hex) return fallback;
  const raw = hex.trim().replace(/^#/, '');

  const expand =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(expand)) return fallback;

  return [
    parseInt(expand.slice(0, 2), 16) / 255,
    parseInt(expand.slice(2, 4), 16) / 255,
    parseInt(expand.slice(4, 6), 16) / 255,
  ];
}

/**
 * The fallback colour for a skill with no palette, by scaling type.
 *
 * Every skill in the shipped catalogue has one, so this is for a hand-added entry
 * — but a bolt with no colour would be black against a dark floor, i.e. invisible,
 * which is indistinguishable from the bug this whole feature fixes.
 */
export function defaultColour(scalingType: string | undefined): [number, number, number] {
  switch (scalingType) {
    case 'ATTRIBUTE':
      // Arcane blue: the closest thing to "a spell" the palette has.
      return [0.42, 0.62, 0.95];
    case 'HYBRID':
      return [0.75, 0.5, 0.9];
    case 'UTILITY':
      return [0.55, 0.85, 0.5];
    default:
      // Weapon skills are struck, not thrown; if one ever draws a bolt, steel.
      return [0.85, 0.82, 0.72];
  }
}

/**
 * Whether a skill throws something, or lands where it stands.
 *
 * A weapon skill is a swing: drawing a bolt for it would say the damage came from
 * across the room. Utility is cast on the self. So only attribute and hybrid
 * skills fly — and a hybrid does because half of it is a spell.
 */
export function throwsProjectile(scalingType: string | undefined): boolean {
  return scalingType === 'ATTRIBUTE' || scalingType === 'HYBRID';
}

// ── Shapes ──────────────────────────────────────────────────────────────────

/**
 * The closed shape vocabulary, mirroring `config.ProjectileShapes` in Go.
 *
 * **Shapes, not elements.** Typed damage does not exist — every hit in the game is
 * physical — so a `fire`/`cold`/`lightning` field would name a system that is not
 * there and promise a resistance nothing reads. A shape promises only what it
 * delivers: what the thing looks like crossing the room.
 *
 * The server never interprets one; `skills.yaml` carries it under `visual_assets`
 * precisely because that block is presentation. A modified client could rename every
 * member and change no outcome.
 */
export const PROJECTILE_SHAPES = ['SHARD', 'ORB', 'ARC', 'BEAM', 'SPRAY', 'NONE'] as const;

export type ProjectileShape = (typeof PROJECTILE_SHAPES)[number];

/** The head geometry a shape asks `bolts.ts` to build. */
export type ProjectileGeometry = 'spike' | 'sphere' | 'sliver' | 'lance' | 'fan' | 'none';

/**
 * Everything a shape decides, in world units and radians.
 *
 * Dimensions are given along three named axes rather than as a Babylon vector so this
 * file stays Babylon-free and so the meaning survives being read: `along` is the
 * flight direction (local +Z after the heading yaw), `across` is lateral, `up` is
 * vertical.
 *
 * Every dimension has to clear `MIN_OUTLINE_WIDTH` (≈0.12 units, one output pixel
 * after the pixelate post-process downsamples). A thinner one is averaged away and
 * nothing reports it — the failure that made the hover highlight look broken for a
 * milestone. `bolt.test.ts` asserts the floor for every shape.
 */
export interface ProjectileVisual {
  shape: ProjectileShape;
  geometry: ProjectileGeometry;
  /** Extent along the flight direction. */
  along: number;
  /** Extent across the flight direction. */
  across: number;
  /** Vertical extent. */
  up: number;
  /** Radians per second the head rolls about its flight axis. 0 for a still head. */
  spin: number;
  /** How many motes trail the head. 0 for a shape that is its own trail. */
  trailCount: number;
  /** Seconds of flight between the head and the mote behind it. */
  trailSpacing: number;
  /**
   * Lateral offset amplitude of the wake, in world units.
   *
   * `alternating` zig-zags a crackle; `spreading` fans out behind the head. Straight
   * is what a solid thrown object does.
   */
  trailJitter: number;
  trailPattern: 'straight' | 'alternating' | 'spreading';
  /** Multipliers on `impactScale`, so the flash has the shape's own proportions. */
  impactAlong: number;
  impactAcross: number;
  impactUp: number;
}

/**
 * Fallback for a shape the catalogue does not name, or names wrongly.
 *
 * `ORB` is exactly what every spell in the game looked like before shapes existed: a
 * 0.42-unit emissive sphere on a shallow arc with a round bloom at the end. Keeping it
 * as the fallback is what makes "adding a skill needs no visual work" true, and it is
 * why an unknown shape is a *wrong* projectile rather than an absent one — the same
 * reasoning that makes a malformed palette a wrong colour rather than black.
 */
export const FALLBACK_SHAPE: ProjectileShape = 'ORB';

/**
 * What each shape looks like and how it behaves.
 *
 * The dimensions are not free choices. One world unit is about `PIXELS_PER_UNIT`
 * device pixels and the post-process renders at 1/`PIXEL_SIZE`, so a shape is about
 * eight output pixels per unit and a character is roughly 30 × 45 device pixels. Two
 * shapes that differ by a tenth of a unit differ by less than one pixel on screen and
 * are the same shape as far as a player is concerned — which is why `bolts.test.ts`
 * measures the built geometry pairwise rather than trusting these numbers to look
 * different.
 */
const VISUALS: Record<ProjectileShape, ProjectileVisual> = {
  // A frost splinter, and the shape this whole feature exists for. Long, narrow and
  // rolling about its own axis, with a tight shrinking sparkle behind it; it shatters
  // upward on impact rather than blooming.
  SHARD: {
    shape: 'SHARD',
    geometry: 'spike',
    along: 1.0,
    across: 0.3,
    up: 0.3,
    spin: 8,
    trailCount: 5,
    trailSpacing: 0.055,
    trailJitter: 0,
    trailPattern: 'straight',
    impactAlong: 0.55,
    impactAcross: 0.55,
    impactUp: 1.5,
  },

  // The original bolt, kept exactly: a round emissive ball with a short soft puff and
  // an isotropic bloom. It is the fallback, so changing these numbers changes every
  // skill the catalogue says nothing about.
  ORB: {
    shape: 'ORB',
    geometry: 'sphere',
    along: 0.42,
    across: 0.42,
    up: 0.42,
    spin: 0,
    trailCount: 3,
    trailSpacing: 0.05,
    trailJitter: 0,
    trailPattern: 'straight',
    impactAlong: 1,
    impactAcross: 1,
    impactUp: 1,
  },

  // A discharge. Short and very thin, spinning fast enough to flicker, with a wake
  // that zig-zags either side of the path — the crackle is the motion, not the head,
  // because at this size a lightning bolt's detail is well under one output pixel. It
  // ends in a flat wide flash.
  ARC: {
    shape: 'ARC',
    geometry: 'sliver',
    along: 0.62,
    across: 0.14,
    up: 0.14,
    spin: 28,
    trailCount: 6,
    trailSpacing: 0.035,
    trailJitter: 0.28,
    trailPattern: 'alternating',
    impactAlong: 1.5,
    impactAcross: 1.5,
    impactUp: 0.35,
  },

  // A held line. Long enough to span a good part of the gap on its own, so it needs
  // no wake at all — it *is* the wake — and it punches through along the flight
  // rather than blooming.
  BEAM: {
    shape: 'BEAM',
    geometry: 'lance',
    along: 2.2,
    across: 0.18,
    up: 0.18,
    spin: 0,
    trailCount: 0,
    trailSpacing: 0,
    trailJitter: 0,
    trailPattern: 'straight',
    impactAlong: 2.2,
    impactAcross: 0.5,
    impactUp: 0.5,
  },

  // A widening front: wide across the flight, flat, tumbling slowly, with a wake that
  // spreads behind it into a cone. Its impact hugs the ground.
  SPRAY: {
    shape: 'SPRAY',
    geometry: 'fan',
    along: 0.34,
    across: 0.95,
    up: 0.2,
    spin: 4,
    trailCount: 8,
    trailSpacing: 0.04,
    trailJitter: 0.22,
    trailPattern: 'spreading',
    impactAlong: 1,
    impactAcross: 1.7,
    impactUp: 0.3,
  },

  // Nothing is thrown. The flash blooms on the caster instead, immediately.
  //
  // This is the shape a nova, a shout and a self-buff need, and its absence was a real
  // fault: nineteen of the fifty-four skills that "throw" are Warcry, Iron Skin,
  // Glacial Nova and their kind, every one of which lobbed a coloured ball at whatever
  // the cursor happened to be over. The impact proportions still apply, so the bloom
  // is round and generous — a self-buff should read from the body outward.
  NONE: {
    shape: 'NONE',
    geometry: 'none',
    along: 0,
    across: 0,
    up: 0,
    spin: 0,
    trailCount: 0,
    trailSpacing: 0,
    trailJitter: 0,
    trailPattern: 'straight',
    impactAlong: 1.25,
    impactAcross: 1.25,
    impactUp: 0.9,
  },
};

// Frozen because `resolveProjectile` hands the *same* object to every bolt of a shape:
// a caller that nudged one field would change every future cast of every skill using it,
// and would do it silently, for the rest of the session.
for (const visual of Object.values(VISUALS)) Object.freeze(visual);
Object.freeze(VISUALS);

/**
 * Resolves a catalogue shape string to what to draw.
 *
 * Two fallbacks, and both matter. An **absent** shape is the common case for a
 * hand-added skill and must keep working, so it resolves to `ORB` — today's
 * behaviour. An **unknown** shape is a typo in content, which is silent by nature, and
 * it resolves to the same thing: a wrong projectile is a bug someone can see, an
 * absent one looks exactly like the skill not working.
 *
 * `scalingType` is here so the caller has one question to ask. A scaling type that
 * throws nothing resolves to `NONE` whatever the shape says — a weapon skill is a
 * swing, and the loader refuses to author a shape on one in the first place.
 */
export function resolveProjectile(
  shape: string | undefined,
  scalingType: string | undefined,
): ProjectileVisual {
  if (!throwsProjectile(scalingType)) return VISUALS.NONE;
  if (shape !== undefined && isProjectileShape(shape)) return VISUALS[shape];
  return VISUALS[FALLBACK_SHAPE];
}

/** Whether a string is a member of the vocabulary. */
export function isProjectileShape(value: string): value is ProjectileShape {
  return (PROJECTILE_SHAPES as readonly string[]).includes(value);
}

/** The visual for a known shape, for tests and for walking the whole vocabulary. */
export function projectileVisual(shape: ProjectileShape): ProjectileVisual {
  return VISUALS[shape];
}

/**
 * How far behind the head, in units of flight time, mote `index` sits.
 *
 * Sampled off the same arc as the head rather than recorded from past frames: a
 * recorded trail has to store positions per bolt per frame and drifts whenever a
 * frame is long, and the arc is a closed form that costs nothing.
 */
export function trailDelay(visual: ProjectileVisual, index: number): number {
  return visual.trailSpacing * (index + 1);
}

/**
 * Lateral offset of mote `index`, in world units, perpendicular to the flight.
 *
 * This is what tells one wake from another at eight output pixels per unit. A straight
 * wake reads as a thrown object, an alternating one as a crackle, a spreading one as a
 * cone — and none of that is legible from the head alone, which is the whole point of
 * having a trail at these sizes.
 */
export function trailOffset(visual: ProjectileVisual, index: number): number {
  if (visual.trailJitter === 0) return 0;
  switch (visual.trailPattern) {
    case 'alternating':
      return index % 2 === 0 ? visual.trailJitter : -visual.trailJitter;
    case 'spreading': {
      // Widens with distance behind the head, alternating side to side so the cone
      // fills rather than curving off to one edge.
      const reach = (index + 1) / visual.trailCount;
      return (index % 2 === 0 ? 1 : -1) * visual.trailJitter * reach;
    }
    default:
      return 0;
  }
}

/**
 * Size of mote `index` as a fraction of the head's own cross-section.
 *
 * Falls away behind the head so the wake reads as one thing with a direction rather
 * than as a row of identical dots. Floored so the last mote still clears
 * `MIN_OUTLINE_WIDTH` — a wake that fades to nothing fades to invisible one mote
 * early.
 */
export function trailMoteScale(visual: ProjectileVisual, index: number): number {
  if (visual.trailCount <= 0) return 0;
  const behind = index / visual.trailCount;
  return Math.max(TRAIL_MOTE_MIN, 1 - 0.5 * behind);
}

/**
 * The smallest a trail mote may be, as a fraction of `TRAIL_MOTE_DIAMETER`.
 *
 * Chosen against the pixel floor rather than for looks: `TRAIL_MOTE_MIN ×
 * TRAIL_MOTE_DIAMETER` has to stay above `MIN_OUTLINE_WIDTH`, or the tail of the wake
 * is averaged away and the trail is shorter than it is written to be. Asserted in
 * `bolt.test.ts`.
 */
export const TRAIL_MOTE_MIN = 0.55;

/** Diameter of the sphere a trail mote is drawn as, before `trailMoteScale`. */
export const TRAIL_MOTE_DIAMETER = 0.26;

/**
 * Diameter of the impact flash before `impactScale` and the shape's proportions.
 *
 * 0.42 is the old bolt's own diameter, kept so `ORB` — the fallback every unshaped
 * skill resolves to — flashes at exactly the size it always did.
 */
export const IMPACT_BASE_DIAMETER = 0.42;

/**
 * The flash's extent on one axis at the end of its life, in world units.
 *
 * Measured at the **peak** deliberately. A flash expands, so its thin axis is under an
 * output pixel for the first frames of a flat one — that reads as a flash arriving. A
 * flash whose thin axis never clears a pixel is invisible, and that is the floor
 * `bolt.test.ts` holds every shape to.
 */
export function impactPeakExtent(visual: ProjectileVisual, axis: 'along' | 'across' | 'up'): number {
  const multiplier =
    axis === 'along' ? visual.impactAlong : axis === 'across' ? visual.impactAcross : visual.impactUp;
  return IMPACT_BASE_DIAMETER * impactScale(1) * multiplier;
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(1, v));
}

function easeOut(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

/**
 * The shape a basic ranged attack flies as when nothing more specific is known.
 *
 * Every monster fires it: `mobs.yaml` has no projectile field, and inventing one per
 * archetype would be authoring work with nothing behind it — an arrow, a bolt of dark
 * magic and a thrown spine all read the same at 30 × 45 device pixels. What a player needs
 * to read is *that* something flew and what it was made of, which is the colour's job.
 */
export const DEFAULT_SHOT_SHAPE: ProjectileShape = 'SHARD';

/**
 * What a **basic** ranged attack flies as, by the shooter's weapon family.
 *
 * A `Record` over the closed `WeaponClass` union, so TypeScript refuses an incomplete map:
 * an eleventh weapon family added to the wire without an entry here is a compile error
 * rather than a bow that silently throws a wand bolt. The same shape `ARCHETYPES` uses, and
 * the reason is the one the stat-label table learned the hard way — a fallback is not
 * coverage.
 *
 * `null` means *this family does not shoot*. Every melee weapon is null, and so is the
 * absence of a weapon: their reach never clears the server's threshold, so no shot event
 * can name them. If one ever does, `shotShapeFor` draws the default shard rather than
 * nothing — a wrong projectile is a bug somebody can see, and no projectile looks exactly
 * like the feature not working.
 *
 * Only two shapes, because only two things are being told apart: an **arrow** is a shard,
 * and a **caster's bolt** is an orb. A staff is a caster's arm here even though it is also
 * a melee-looking silhouette — its `attack_range` is 11, so the game already treats it as
 * one.
 */
const SHOT_SHAPES: Record<WeaponClass, ProjectileShape | null> = {
  BOW: 'SHARD',
  WAND: 'ORB',
  STAFF: 'ORB',
  SWORD_1H: null,
  SWORD_2H: null,
  AXE_1H: null,
  AXE_2H: null,
  MACE_1H: null,
  MACE_2H: null,
  DAGGER: null,
};

/**
 * The shape a shooter's basic attack flies as, given the `weapon_type` on its snapshot.
 *
 * `fallback` is what a monster fires and what anything unrecognised falls back to — the
 * server has already said a shot happened, so the only question left is what to draw, and
 * the answer is never "nothing".
 */
export function shotShapeFor(
  weapon: string | undefined,
  fallback: ProjectileShape,
): ProjectileShape {
  if (weapon === undefined) return fallback;
  const shape = SHOT_SHAPES[weapon as WeaponClass];
  return shape ?? fallback;
}
